import { DLODropdownOption, ApiDataTransformed, EmployeeRowData, RolesByType, ScheduleApiEntry, ShiftData, ShiftApiData } from '../types';
import { fromBackendWeek } from '../../../utils/weekTransforms';
/**
 * Transforms employees API data to dropdown options
 */
export const getEmployeeOptions = (employees: any[]): DLODropdownOption[] => {
  if (!employees || !Array.isArray(employees)) {
    return [];
  }

  return employees.map((employee: any) => {
    // Create full name from First_Name and Last_Name
    const fullName = `${employee.First_Name || ''} ${employee.Last_Name || ''}`.trim();

    return {
      id: employee.Id?.toString() || employee.GPID || '',
      label: fullName || employee.GPID || 'Unknown Employee',
      value: employee.Id?.toString() || employee.GPID || '',
      gpid: employee.GPID // Include GPID in the dropdown option
    };
  }).sort((a: DLODropdownOption, b: DLODropdownOption) => a.label.localeCompare(b.label));
};

/**
 * Transforms employee roles API data to dropdown options
 */
export const getRoleOptions = (employeeRoles: any): DLODropdownOption[] => {
  if (!employeeRoles?.data || !Array.isArray(employeeRoles.data)) {
    return [];
  }

  // Deduplicate by roleId (numeric id), falling back to name for roles without an id
  const seen = new Set<string>();
  const roleOptions: DLODropdownOption[] = [];

  employeeRoles.data.forEach((role: any) => {
    const roleName = role.loRole;
    const roleId = role.roleId ?? null;
    // Use roleId as the unique key when available, otherwise the name
    const uniqueKey = roleId != null ? String(roleId) : roleName;

    if (!seen.has(uniqueKey)) {
      seen.add(uniqueKey);
      roleOptions.push({
        id: roleId != null ? String(roleId) : roleName,
        label: roleName,
        value: roleId != null ? String(roleId) : roleName,
        roleId: roleId,
      });
    }
  });

  // Sort roles by label for better UX
  return roleOptions.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Transforms shift times API data to dropdown options
 */
export const getShiftOptions = (shiftTimes: any): DLODropdownOption[] => {
  // Handle both a flat array response and a wrapped { data: [...] } response
  const data: any[] = Array.isArray(shiftTimes)
    ? shiftTimes
    : Array.isArray(shiftTimes?.data)
      ? shiftTimes.data
      : null;

  if (!data) {
    if (shiftTimes?.loading) {
      return [{ id: 'loading', label: 'Loading shifts...', value: 'loading' }];
    }
    return [];
  }

  return data
    .filter((shift: any) => shift && shift.time_value)
    .map((shift: any) => {
    // Format time from API response
    const formatTime = (timeValue: string, timeFormat: string) => {
      if (!timeValue) return '';

      // Parse time_value which is in format "HH:MM:SS.0000000"
      const [hours, minutes] = timeValue.split(':');
      const hour24 = parseInt(hours, 10);

      // Convert to 12-hour format with provided AM/PM
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

      return `${hour12.toString().padStart(2, '0')}:${minutes} ${timeFormat}`;
    };

    // Create label in format "05:30 AM (5X8)"
    const formattedTime = formatTime(shift.time_value, shift.time_format);
    const shiftType = shift.shift_type ? shift.shift_type.toUpperCase() : '';
    const label = `${formattedTime}${shiftType ? ` (${shiftType})` : ''}`;

    // Use server id when present; fall back to a composite key from time/type
    const shiftId = shift.id != null
      ? String(shift.id)
      : `${shift.time_value}-${shift.shift_type || ''}-${shift.time_format || ''}`;

    return {
      id: shiftId,
      label: label,
      value: JSON.stringify({
        id: shiftId,
        shift_type: shift.shift_type,
        time_format: shift.time_format,
        time_value: shift.time_value
      })
    };
  }).sort((a: DLODropdownOption, b: DLODropdownOption) => a.label.localeCompare(b.label));
};

/**
 * Helper function to extract shift data from dropdown value
 */
const normalizeShiftTimeValue = (timeValue?: string): string => {
  if (!timeValue) return '';

  const cleanTime = timeValue.split('.')[0];
  const timeParts = cleanTime.split(':');

  if (timeParts.length === 3) {
    return cleanTime;
  }

  if (timeParts.length === 2) {
    return `${cleanTime}:00`;
  }

  return cleanTime;
};

const parseShiftForComparison = (shift: any): {
  timeValue: string;
  shiftType: string;
  timeFormat: string;
  hasMetadata: boolean;
} => {
  if (!shift) {
    return { timeValue: '', shiftType: '', timeFormat: '', hasMetadata: false };
  }

  if (typeof shift === 'object' && shift.time_value) {
    const shiftType = (shift.shift_type || '').toLowerCase();
    const timeFormat = (shift.time_format || '').toLowerCase();

    return {
      timeValue: normalizeShiftTimeValue(shift.time_value),
      shiftType,
      timeFormat,
      hasMetadata: !!(shiftType || timeFormat),
    };
  }

  if (typeof shift === 'string') {
    const raw = shift.trim();

    if (raw.startsWith('{') && raw.endsWith('}')) {
      try {
        return parseShiftForComparison(JSON.parse(raw));
      } catch {
        // Fall back to plain time comparison.
      }
    }

    return {
      timeValue: normalizeShiftTimeValue(raw),
      shiftType: '',
      timeFormat: '',
      hasMetadata: false,
    };
  }

  return { timeValue: '', shiftType: '', timeFormat: '', hasMetadata: false };
};

const areComparableShiftsEqual = (leftShift: any, rightShift: any): boolean => {
  const left = parseShiftForComparison(leftShift);
  const right = parseShiftForComparison(rightShift);

  if (!left.timeValue && !right.timeValue) {
    return true;
  }

  if (!left.timeValue || !right.timeValue) {
    return false;
  }

  if (left.timeValue !== right.timeValue) {
    return false;
  }

  if (left.hasMetadata && right.hasMetadata) {
    return left.shiftType === right.shiftType && left.timeFormat === right.timeFormat;
  }

  return true;
};

export const getShiftData = (shift: DLODropdownOption | null): ShiftData | null => {
  if (!shift || !shift.value) return null;

  try {
    // If value is JSON, parse it
    return JSON.parse(shift.value);
  } catch (error) {
    // If parsing fails, assume it's the old format (just ID)
    console.warn('Could not parse shift value as JSON, using fallback:', shift.value);
    return {
      id: shift.id,
      shift_type: '',
      time_format: '',
      time_value: ''
    };
  }
};

/**
 * Merges original roles with current filter state
 */
export const getMergedRolesWithFilterState = (
  originalRoles: RolesByType,
  currentFilteredRoles: RolesByType
): RolesByType => {
  const mergedRoles: RolesByType = {
    DSD: [],
    'NON-DSD': [],
    Retail: [],
    Other: [],
  };

  // Process each category
  Object.keys(originalRoles).forEach(categoryKey => {
    const category = categoryKey as keyof RolesByType;
    const originalCategoryRoles = originalRoles[category] || [];
    const filteredCategoryRoles = currentFilteredRoles[category] || [];

    // For each original role, check if it has a filter state
    originalCategoryRoles.forEach(originalRole => {
      const filteredRole = filteredCategoryRoles.find(fr => fr.id === originalRole.id || fr.name === originalRole.name);

      mergedRoles[category].push({
        ...originalRole,
        enabled: filteredRole ? filteredRole.enabled : originalRole.enabled, // Use filtered state if exists, otherwise use original
      });
    });
  });

  return mergedRoles;
};

/**
 * Transforms API data to table format for Schedule Hours (FCST vs SCHD)
 */
export const transformApiDataToTableFormat = (apiData: any): ApiDataTransformed[] => {
  if (!apiData || !apiData.summary?.hasData) {
    return [];
  }

  const transformedRows: ApiDataTransformed[] = [];
  const responseData = apiData.data?.data || [];

  // Check if we have data array
  if (!Array.isArray(responseData) || responseData.length === 0) {
    return [];
  }

  // Group data by roleType and role name
  const groupedData: Record<string, Record<string, any[]>> = {};

  responseData.forEach((item: any) => {
    const roleType = item.roleType || 'Other';
    const roleName = item.role || 'Unknown Role';

    if (!groupedData[roleType]) {
      groupedData[roleType] = {};
    }
    if (!groupedData[roleType][roleName]) {
      groupedData[roleType][roleName] = [];
    }
    groupedData[roleType][roleName].push(item);
  });

  // Process each role type that has data
  Object.keys(groupedData).forEach(roleType => {
    const roleTypeData = groupedData[roleType];

    // Create table rows for each role within this roleType
    Object.keys(roleTypeData).forEach(roleName => {
      const roleItems = roleTypeData[roleName];

      // Initialize weekly data structure
      const weeklyData = {
        sunday: { fcst: 0, schd: 0 },
        monday: { fcst: 0, schd: 0 },
        tuesday: { fcst: 0, schd: 0 },
        wednesday: { fcst: 0, schd: 0 },
        thursday: { fcst: 0, schd: 0 },
        friday: { fcst: 0, schd: 0 },
        saturday: { fcst: 0, schd: 0 },
      };

      // Map day numbers to day names (1=Sunday, 2=Monday, etc.)
      const dayMapping: Record<number, keyof typeof weeklyData> = {
        1: 'sunday',
        2: 'monday',
        3: 'tuesday',
        4: 'wednesday',
        5: 'thursday',
        6: 'friday',
        7: 'saturday'
      };

      // Populate weekly data from API response
      roleItems.forEach(item => {
        const dayName = dayMapping[item.dayNumber];
        if (dayName) {
          weeklyData[dayName].fcst = parseFloat(item.forecastHours || 0);
          weeklyData[dayName].schd = parseFloat(item.scheduledHours || 0);
        }
      });

      // Calculate totals
      const totalFcst = Object.values(weeklyData).reduce((sum, day) => sum + day.fcst, 0);
      const totalSchd = Object.values(weeklyData).reduce((sum, day) => sum + day.schd, 0);
      const variance = totalFcst - totalSchd;

      // Create table row
      const tableRow: ApiDataTransformed = {
        id: `${roleType}_${roleName}`,
        roleName: `${roleName} (${roleType})`,
        ...weeklyData,
        total: { fcst: totalFcst, schd: totalSchd },
        variance: variance
      };

      transformedRows.push(tableRow);
    });
  });

  console.log('Transformed table data:', transformedRows);
  return transformedRows;
};

/**
 * Transforms API data to table format for Actual Hours (EST vs ACTUAL)
 * When isActual=true, API returns: estimatedHours and actualHours
 */
export const transformActualHoursToTableFormat = (apiData: any): ApiDataTransformed[] => {
  if (!apiData || !apiData.summary?.hasData) {
    return [];
  }

  const transformedRows: ApiDataTransformed[] = [];
  const responseData = apiData.data?.data || [];

  // Check if we have data array
  if (!Array.isArray(responseData) || responseData.length === 0) {
    return [];
  }

  // Group data by roleType and role name
  const groupedData: Record<string, Record<string, any[]>> = {};

  responseData.forEach((item: any) => {
    const roleType = item.roleType || 'Other';
    const roleName = item.role || 'Unknown Role';

    if (!groupedData[roleType]) {
      groupedData[roleType] = {};
    }
    if (!groupedData[roleType][roleName]) {
      groupedData[roleType][roleName] = [];
    }
    groupedData[roleType][roleName].push(item);
  });

  // Process each role type that has data
  Object.keys(groupedData).forEach(roleType => {
    const roleTypeData = groupedData[roleType];

    // Create table rows for each role within this roleType
    Object.keys(roleTypeData).forEach(roleName => {
      const roleItems = roleTypeData[roleName];

      // Initialize weekly data structure with EST (estimatedHours) and ACTUAL (actualHours)
      const weeklyData = {
        sunday: { fcst: 0, schd: 0 },
        monday: { fcst: 0, schd: 0 },
        tuesday: { fcst: 0, schd: 0 },
        wednesday: { fcst: 0, schd: 0 },
        thursday: { fcst: 0, schd: 0 },
        friday: { fcst: 0, schd: 0 },
        saturday: { fcst: 0, schd: 0 },
      };

      // Map day numbers to day names (1=Sunday, 2=Monday, etc.)
      const dayMapping: Record<number, keyof typeof weeklyData> = {
        1: 'sunday',
        2: 'monday',
        3: 'tuesday',
        4: 'wednesday',
        5: 'thursday',
        6: 'friday',
        7: 'saturday'
      };

      // Populate weekly data from API response
      // fcst = estimatedHours (EST), schd = actualHours (ACTUAL)
      roleItems.forEach(item => {
        const dayName = dayMapping[item.dayNumber];
        if (dayName) {
          weeklyData[dayName].fcst = parseFloat(item.estimatedHours || 0); // EST
          weeklyData[dayName].schd = parseFloat(item.actualHours || 0); // ACTUAL
        }
      });

      // Calculate totals
      const totalEst = Object.values(weeklyData).reduce((sum, day) => sum + day.fcst, 0);
      const totalActual = Object.values(weeklyData).reduce((sum, day) => sum + day.schd, 0);
      const variance = totalEst - totalActual;

      // Create table row (reusing same structure but with EST/ACTUAL semantics)
      const tableRow: ApiDataTransformed = {
        id: `${roleType}_${roleName}`,
        roleName: `${roleName} (${roleType})`,
        ...weeklyData,
        total: { fcst: totalEst, schd: totalActual },
        variance: variance
      };

      transformedRows.push(tableRow);
    });
  });

  console.log('Transformed actual hours table data:', transformedRows);
  return transformedRows;
};

/**
 * Extracts roles from API data for the filter modal
 */
export const extractRolesByType = (apiData: any, tableData?: ApiDataTransformed[]): RolesByType => {
  const rolesByType: RolesByType = {
    DSD: [],
    'NON-DSD': [],
    Retail: [],
    Other: [],
  };

  if (!apiData || !apiData.data?.data) {
    return rolesByType;
  }

  const responseData = apiData.data.data || [];

  // Check if we have data array
  if (!Array.isArray(responseData) || responseData.length === 0) {
    return rolesByType;
  }

  // Group roles by roleType
  const rolesByTypeMap: Record<string, Set<string>> = {
    'DSD': new Set(),
    'NON-DSD': new Set(),
    'Retail': new Set(),
    'Other': new Set(),
  };

  responseData.forEach((item: any) => {
    const roleType = item.roleType || 'Other';
    const roleName = item.role || 'Unknown Role';

    if (rolesByTypeMap[roleType]) {
      rolesByTypeMap[roleType].add(roleName);
    } else {
      // If roleType doesn't match our predefined categories, add to 'Other'
      rolesByTypeMap['Other'].add(roleName);
    }
  });

  // Helper function to check if a role has zero variance
  const hasZeroVariance = (roleName: string): boolean => {
    if (!tableData || tableData.length === 0) {
      return false; // Default to enabled if no table data available
    }
    
    const matchingTableRow = tableData.find(row => {
      const normalizedRowName = row.roleName.toLowerCase().trim();
      const normalizedRoleName = roleName.toLowerCase().trim();
      
      // Direct match
      if (normalizedRowName === normalizedRoleName) {
        return true;
      }
      
      // Partial match for role name variations
      if (normalizedRowName.includes(normalizedRoleName) || 
          normalizedRoleName.includes(normalizedRowName)) {
        return true;
      }
      
      return false;
    });
    
    // If matching row found, check if forecast total is effectively zero
    if (matchingTableRow) {
      return Math.abs(matchingTableRow.total.fcst) <= 0.001;
    }
    
    return false; // Default to enabled if role not found in table data
  };

  // Convert Sets to arrays and create role objects
  Object.keys(rolesByTypeMap).forEach(roleType => {
    const mappedRoleType = roleType as keyof RolesByType;
    const uniqueRoles = Array.from(rolesByTypeMap[roleType]);

    uniqueRoles.forEach((roleName, index) => {
      // Check if this role has zero variance and should be disabled by default
      const shouldDisable = hasZeroVariance(roleName);
      
      rolesByType[mappedRoleType].push({
        id: `${roleType}_${roleName}_${index}`,
        name: roleName,
        enabled: !shouldDisable, // Disable roles with zero variance by default
      });
    });
  });

  console.log('Extracted roles by type with variance filtering:', rolesByType);
  return rolesByType;
};

/**
 * Formats period week year display
 */
export const getPeriodWeekYearDisplay = (createSchedulePeriod?: number, createScheduleWeek?: number, createScheduleYear?: number): string => {
  const currentYear = new Date().getFullYear();
  if (createSchedulePeriod && createScheduleWeek && createScheduleYear) {
    return `P${createSchedulePeriod} W${fromBackendWeek(createScheduleWeek).week} Y${createScheduleYear}`;
  }
  return `P0 W0 Y${currentYear}`;
};

/**
 * Returns Saturday 23:59 of the current week using device local time
 */
export function getCurrentWeekCutoff(): Date {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysUntilSaturday = 6 - currentDay; // Days to add to get to Saturday
  
  const saturday = new Date(now);
  saturday.setDate(now.getDate() + daysUntilSaturday);
  saturday.setHours(23, 59, 59, 999); // Set to 23:59:59.999
  
  return saturday;
}

/**
 * Compares target week with current week
 * @param targetWeek - The target week to compare
 * @param currentWeek - The current week
 * @returns -1 (past), 0 (current), 1 (future)
 */
export function compareWeek(
  targetWeek: { period: number, week: number, year: number },
  currentWeek: { period: number, week: number, year: number }
): -1 | 0 | 1 {
  // Compare by year first
  if (targetWeek.year < currentWeek.year) return -1;
  if (targetWeek.year > currentWeek.year) return 1;
  
  // Same year, compare by period
  if (targetWeek.period < currentWeek.period) return -1;
  if (targetWeek.period > currentWeek.period) return 1;
  
  // Same period, compare by week
  if (targetWeek.week < currentWeek.week) return -1;
  if (targetWeek.week > currentWeek.week) return 1;
  
  // Same week
  return 0;
}

/**
 * Returns Saturday 23:59 of the week immediately preceding the target week
 * @param targetWeek - The target week object with period, week, and year
 * @returns Date object set to Saturday 23:59:00.000 of the week before targetWeek
 */
export function getCutoffForTargetWeek(targetWeek: { period: number, week: number, year: number }): Date {
  // First, we need to derive a canonical date that belongs to the target week
  // We'll calculate the start of the target week (Sunday 00:00)
  
  // Calculate the calendar week number for the target week
  // Using the existing formula: calculatedWeek = (period - 1) * 4 + week
  const targetCalendarWeek = (targetWeek.period - 1) * 4 + targetWeek.week;
  
  // For simplicity, we'll use January 1st as the reference point and calculate from there
  // This assumes weeks start on Sunday (0) and each period has 4 weeks
  const yearStart = new Date(targetWeek.year, 0, 1); // January 1st of target year
  const yearStartDay = yearStart.getDay(); // Day of week for Jan 1st (0 = Sunday)
  
  // Calculate days to first Sunday of the year
  const daysToFirstSunday = yearStartDay === 0 ? 0 : 7 - yearStartDay;
  
  // Calculate the start date of the target week (Sunday 00:00)
  const startOfTargetWeek = new Date(yearStart);
  startOfTargetWeek.setDate(yearStart.getDate() + daysToFirstSunday + (targetCalendarWeek - 1) * 7);
  startOfTargetWeek.setHours(0, 0, 0, 0);
  
  // Calculate the cutoff date: start of target week - 1 day = Saturday of previous week
  const cutoffDate = new Date(startOfTargetWeek);
  cutoffDate.setDate(cutoffDate.getDate() - 1);
  
  // Set time to 23:59:00.000
  cutoffDate.setHours(23, 59, 0, 0);
  
  return cutoffDate;
}

/**
 * Finds orphaned schedule records from the initial GET /dlo/schedule response.
 * An orphaned record is one where:
 *   - The employee (by GPID) does NOT exist in the current employee list, OR
 *   - The shift does NOT exist in the current shift list
 * These records must be sent back in the POST payload with schdHours=0 and actualHours=0
 * so the backend can clear them out.
 *
 * @param initialSchedules - Raw records from GET /dlo/schedule (unfiltered)
 * @param employeeOptions  - Current employee dropdown options (from getEmployeeOptions)
 * @param shiftOptions     - Current shift dropdown options (from getShiftOptions)
 * @param locationData     - Location metadata to attach
 * @param periodData       - Period/week/year metadata to attach
 * @returns ScheduleApiEntry[] with schdHours=0 and actualHours=0 for each orphaned day
 */
export const getOrphanedScheduleEntries = (
  initialSchedules: any[],
  employeeOptions: DLODropdownOption[],
  shiftOptions: DLODropdownOption[],
  locationData?: { locId?: string; locationName?: string },
  periodData?: { period?: number; week?: number; year?: number }
): ScheduleApiEntry[] => {
  if (!initialSchedules || !Array.isArray(initialSchedules) || initialSchedules.length === 0) {
    return [];
  }

  const result: ScheduleApiEntry[] = [];

  // Build a Set of employee GPIDs for fast lookup
  const employeeGpids = new Set<string>();
  employeeOptions.forEach(emp => {
    if (emp.gpid) employeeGpids.add(emp.gpid);
    if (emp.id) employeeGpids.add(emp.id);
    if (emp.value) employeeGpids.add(emp.value);
  });

  // Build a Set of valid shift keys (time_value + shift_type + time_format) for fast lookup
  const validShiftKeys = new Set<string>();
  const validShiftTimeValues = new Set<string>();
  shiftOptions.forEach(shift => {
    try {
      const shiftData = JSON.parse(shift.value);
      if (shiftData.time_value) {
        const normalizedTime = normalizeShiftTimeValue(shiftData.time_value);
        const key = `${normalizedTime}-${(shiftData.shift_type || '').toLowerCase()}-${(shiftData.time_format || '').toLowerCase()}`;
        validShiftKeys.add(key);
        validShiftTimeValues.add(normalizedTime);
      }
    } catch (e) {
      // skip non-JSON shift values
    }
  });

  // Helper: check if a schedule record's shift matches any valid shift
  const isShiftValid = (shift: any): boolean => {
    if (!shift) return true; // null shift is acceptable (no shift assigned)

    const parsedShift = parseShiftForComparison(shift);

    if (!parsedShift.timeValue) return true; // no time to compare

    if (!parsedShift.hasMetadata) {
      return validShiftTimeValues.has(parsedShift.timeValue);
    }

    const key = `${parsedShift.timeValue}-${parsedShift.shiftType}-${parsedShift.timeFormat}`;
    return validShiftKeys.has(key);
  };

  // Helper: check if a schedule record's employee is in the employee list
  const isEmployeeValid = (record: any): boolean => {
    const gpid = record.employeeGpid || '';
    return employeeGpids.has(gpid);
  };

  // Helper to normalize time value
  const normalizeTimeValue = (timeValue: string): string => {
    if (!timeValue) return '';
    const cleanTime = timeValue.split('.')[0];
    const timeParts = cleanTime.split(':');
    if (timeParts.length === 3) return cleanTime;
    if (timeParts.length === 2) return `${cleanTime}:00`;
    return cleanTime;
  };

  initialSchedules.forEach((record: any) => {
    const employeeValid = isEmployeeValid(record);
    const shiftValid = isShiftValid(record.shift);

    // If both employee and shift are valid, this is NOT an orphan
    if (employeeValid && shiftValid) return;

    console.log(`[OrphanDetection] Orphaned record found: employee="${record.employee}" (GPID=${record.employeeGpid}), role="${record.role}", dayNum=${record.dayNum}, reason=${!employeeValid ? 'employee not in list' : 'shift not in list'}`);

    // Build shift API data from the record's shift.
    // Handle both object format (new API) and string format (e.g., "02:00:00" from GET API).
    // Preserving the original shift is critical: backend matches by (emp, role, day, shift),
    // so sending shift:null when the original record had a real shift would create a separate
    // no-shift row instead of zeroing out the original record (e.g., for an invalid employee
    // that the user cleared via the UI).
    let shiftApiData: ShiftApiData | null = null;
    if (record.shift && typeof record.shift === 'object' && record.shift.time_value) {
      shiftApiData = {
        shift_type: record.shift.shift_type || '',
        time_format: record.shift.time_format || '',
        time_value: normalizeShiftTimeValue(record.shift.time_value)
      };
    } else if (typeof record.shift === 'string' && record.shift.trim() !== '') {
      // Look up full shift metadata (shift_type / time_format) from shiftOptions
      const timeStr = normalizeShiftTimeValue(record.shift);
      const matchedShifts = shiftOptions.filter(opt => {
        try {
          const data = JSON.parse(opt.value);
          return data.time_value && normalizeShiftTimeValue(data.time_value) === timeStr;
        } catch {
          return false;
        }
      });
      if (matchedShifts.length === 1) {
        try {
          const data = JSON.parse(matchedShifts[0].value);
          shiftApiData = {
            shift_type: data.shift_type || '',
            time_format: data.time_format || '',
            time_value: normalizeShiftTimeValue(data.time_value)
          };
        } catch {
          shiftApiData = { shift_type: '', time_format: '', time_value: timeStr };
        }
      } else {
        // When the backend only returns a plain time and multiple AM/PM variants exist,
        // preserve the raw time instead of guessing a specific variant.
        shiftApiData = { shift_type: '', time_format: '', time_value: timeStr };
      }
    }

    const entry: ScheduleApiEntry = {
      employee: record.employee || '',
      employeeGpid: record.employeeGpid || '',
      role: record.role || '',
      roleId: record.roleId ?? null,
      dayNum: record.dayNum,
      schdHours: 0,
      actualHours: 0,
      shift: shiftApiData,
      locId: locationData?.locId,
      locationName: locationData?.locationName,
      period: periodData?.period,
      week: periodData?.week,
      year: periodData?.year
    };

    result.push(entry);
  });

  if (result.length > 0) {
    console.log(`[OrphanDetection] Found ${result.length} orphaned schedule entries to zero out`);
  }

  return result;
};

/**
 * Transforms employee rows to API format for saving
 */
export const transformEmployeeRowsToAPIFormat = (
  rows: EmployeeRowData[], 
  locationData?: { locId?: string; locationName?: string },
  periodData?: { period?: number; week?: number; year?: number },
  deletedRows?: EmployeeRowData[],
  originalScheduleData?: any
): ScheduleApiEntry[] => {
  const result: ScheduleApiEntry[] = [];

  // Map weekday fields back to dayNum values
  const fieldToDayNum: { [key: string]: number } = {
    'sunday': 1,
    'monday': 2,
    'tuesday': 3,
    'wednesday': 4,
    'thursday': 5,
    'friday': 6,
    'saturday': 7
  };

  // Helper function to normalize time value to HH:MM:SS format
  const normalizeTimeValue = (timeValue: string): string => {
    if (!timeValue) return '';
    
    // Remove milliseconds if present (e.g., "22:00:00.0000000" -> "22:00:00")
    const cleanTime = timeValue.split('.')[0];
    
    // Ensure it's in HH:MM:SS format
    const timeParts = cleanTime.split(':');
    if (timeParts.length === 3) {
      return cleanTime;
    } else if (timeParts.length === 2) {
      return `${cleanTime}:00`;
    }
    
    return cleanTime;
  };

  rows.forEach((row) => {
    // Skip rows without employee or role
    if (!row.employee || !row.role) {
      return;
    }

    // Get shift data from the shift value (stored as JSON string)
    let shiftData = null;
    if (row.shift && row.shift.value) {
      try {
        shiftData = JSON.parse(row.shift.value);
      } catch (e) {
        console.warn('Failed to parse shift data for row:', row.id, row.shift.value);
      }
    }

    // Create entries for each day that has hours > 0 OR had hours > 0 previously
    const dayFields = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    // Helper to get original hours for this employee, role, and day
    const getOriginalHours = (dayField: string): number => {
      if (!originalScheduleData?.data || !Array.isArray(originalScheduleData.data)) {
        return 0;
      }
      
      const dayNumMap: { [key: string]: number } = {
        'sunday': 1,
        'monday': 2,
        'tuesday': 3,
        'wednesday': 4,
        'thursday': 5,
        'friday': 6,
        'saturday': 7
      };
      
      const dayNum = dayNumMap[dayField];
      const employeeGpid = row.employee?.gpid || row.employee?.id;
      const roleName = row.role?.label || row.role?.value;
      
      const originalEntry = originalScheduleData.data.find((entry: any) => 
        entry.employeeGpid === employeeGpid &&
        entry.role === roleName &&
        entry.dayNum === dayNum &&
        areComparableShiftsEqual(entry.shift, shiftData)
      );
      
      return originalEntry ? (originalEntry.schdHours || 0) : 0;
    };
    
    dayFields.forEach((dayField) => {
      const hours = parseFloat(row[dayField as keyof EmployeeRowData] as string || '0');
      const originalHours = getOriginalHours(dayField);
      
      // Include if current hours > 0 OR original hours were > 0 (to handle updates from positive to 0)
      if (hours > 0 ||(hours == 0 && originalHours > 0)) {
        const dayNum = fieldToDayNum[dayField];
        
        const scheduleEntry: ScheduleApiEntry = {
          employee: row.employee!.label || row.employee!.value,
          employeeGpid: row.employee!.gpid || row.employee!.id,
          role: row.role!.label || row.role!.value,
          roleId: row.role!.roleId ?? null,
          dayNum: dayNum,
          schdHours: hours,
          actualHours: 0, // Default to 0 for schedule data
          shift: shiftData ? {
            shift_type: shiftData.shift_type,
            time_format: shiftData.time_format,
            time_value: normalizeTimeValue(shiftData.time_value)
          } : null,
          locId: locationData?.locId,
          locationName: locationData?.locationName,
          period: periodData?.period,
          week: periodData?.week,
          year: periodData?.year
        };

        result.push(scheduleEntry);
      }
    });
  });

  console.log('Transformed employee rows to API format:', result);
  
  // Handle deleted rows - only include entries that had hours > 0, and reset them to 0
  // IMPORTANT: Build a Set of active entry keys so we skip deleted entries that duplicate an active entry.
  // This prevents conflicts when split roles are restructured (old row goes to deletedRows,
  // new row stays active — both map to the same employee+role+shift+day).
  const activeEntryKeys = new Set<string>();
  result.forEach(entry => {
    const shiftKey = entry.shift
      ? `${entry.shift.time_value || ''}-${(entry.shift.shift_type || '').toLowerCase()}-${(entry.shift.time_format || '').toLowerCase()}`
      : 'no-shift';
    activeEntryKeys.add(`${entry.employeeGpid}-${entry.role}-${entry.dayNum}-${shiftKey}`);
  });

  if (deletedRows && deletedRows.length > 0) {
    console.log('Processing deleted employee rows:', deletedRows);
    
    deletedRows.forEach((row) => {
      if (!row.employee || !row.role) {
        console.warn('Skipping deleted row with missing employee or role:', row);
        return;
      }

      const shiftData = getShiftData(row.shift);

      // Process each day of the week
      Object.entries(fieldToDayNum).forEach(([field, dayNum]) => {
        const dayValue = row[field as keyof EmployeeRowData];
        const hoursValue = typeof dayValue === 'string' ? parseFloat(dayValue) || 0 : 0;

        // Only include if the deleted row had hours > 0 for this day
        if (hoursValue > 0) {
          // Build key to check against active entries — skip if active row already covers this combo
          const deletedShiftKey = shiftData
            ? `${normalizeTimeValue(shiftData.time_value) || ''}-${(shiftData.shift_type || '').toLowerCase()}-${(shiftData.time_format || '').toLowerCase()}`
            : 'no-shift';
          const employeeGpid = row.employee!.gpid || row.employee!.id;
          const roleName = row.role!.label || row.role!.value;
          const deletedKey = `${employeeGpid}-${roleName}-${dayNum}-${deletedShiftKey}`;

          if (activeEntryKeys.has(deletedKey)) {
            console.log(`Skipping deleted row entry for ${row.employee!.label} - ${row.role!.label} on day ${dayNum} (active row already covers this combo)`);
            return;
          }

       const scheduleEntry: ScheduleApiEntry = {
          employee: row.employee!.label || row.employee!.value,
          employeeGpid: row.employee!.gpid || row.employee!.id,
          role: row.role!.label || row.role!.value,
          roleId: row.role!.roleId ?? null,
          dayNum: dayNum,
          schdHours: 0, // Reset scheduled hours to 0
          actualHours: 0, // Default to 0 for schedule data
          shift: shiftData ? {
            shift_type: shiftData.shift_type,
            time_format: shiftData.time_format,
            time_value: normalizeTimeValue(shiftData.time_value)
          } : null,
          locId: locationData?.locId,
          locationName: locationData?.locationName,
          period: periodData?.period,
          week: periodData?.week,
          year: periodData?.year
        };

          result.push(scheduleEntry);
          console.log(`Added deleted row entry for ${row.employee!.label} - ${row.role!.label} on day ${dayNum} (reset to 0 hours)`);
        }
      });
    });
  }

  return result;
};

/**
 * Transforms actual hours employee rows to API format for saving
 */
export const transformActualHoursToAPIFormat = (
  rows: EmployeeRowData[], 
  locationData?: { locId?: string; locationName?: string },
  periodData?: { period?: number; week?: number; year?: number },
  deletedRows?: EmployeeRowData[],
  originalScheduleData?: any
): ScheduleApiEntry[] => {
  const result: ScheduleApiEntry[] = [];

  console.log('=== Debug transformActualHoursToAPIFormat ===');
  console.log('Input rows:', rows);
  console.log('Number of rows:', rows.length);

  // Map weekday fields back to dayNum values
  const fieldToDayNum: { [key: string]: number } = {
    'sunday': 1,
    'monday': 2,
    'tuesday': 3,
    'wednesday': 4,
    'thursday': 5,
    'friday': 6,
    'saturday': 7
  };

  // Helper function to normalize time value to HH:MM:SS format
  const normalizeTimeValue = (timeValue: string): string => {
    if (!timeValue) return '';
    
    // Remove milliseconds if present (e.g., "22:00:00.0000000" -> "22:00:00")
    const cleanTime = timeValue.split('.')[0];
    
    // Ensure it's in HH:MM:SS format
    const timeParts = cleanTime.split(':');
    if (timeParts.length === 3) {
      return cleanTime;
    } else if (timeParts.length === 2) {
      return `${cleanTime}:00`;
    }
    
    return cleanTime;
  };

  rows.forEach((row, index) => {
    console.log(`Processing row ${index}:`, row);
    console.log(`Row employee:`, row.employee);
    console.log(`Row role:`, row.role);
    
    // Skip rows without employee or role
    if (!row.employee || !row.role) {
      console.log(`Skipping row ${index} - missing employee or role`);
      return;
    }

    // Get shift data from the shift value (stored as JSON string)
    let shiftData = null;
    if (row.shift && row.shift.value) {
      try {
        shiftData = JSON.parse(row.shift.value);
      } catch (e) {
        console.warn('Failed to parse shift data for row:', row.id, row.shift.value);
      }
    }

    // Create entries for each day that has hours > 0 OR had hours > 0 previously
    const dayFields = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    // Helper to get original hours for this employee, role, and day
    const getOriginalActualHours = (dayField: string): number => {
      if (!originalScheduleData?.data || !Array.isArray(originalScheduleData.data)) {
        return 0;
      }
      
      const dayNumMap: { [key: string]: number } = {
        'sunday': 1,
        'monday': 2,
        'tuesday': 3,
        'wednesday': 4,
        'thursday': 5,
        'friday': 6,
        'saturday': 7
      };
      
      const dayNum = dayNumMap[dayField];
      const employeeGpid = row.employee?.gpid || row.employee?.id;
      const roleName = row.role?.label || row.role?.value;
      
      const originalEntry = originalScheduleData.data.find((entry: any) => 
        entry.employeeGpid === employeeGpid &&
        entry.role === roleName &&
        entry.dayNum === dayNum &&
        areComparableShiftsEqual(entry.shift, shiftData)
      );
      
      return originalEntry ? (originalEntry.actualHours || 0) : 0;
    };
    
    dayFields.forEach((dayField) => {
      const hourValue = row[dayField as keyof EmployeeRowData] as string;
      const hours = parseFloat(hourValue || '0');
      const originalHours = getOriginalActualHours(dayField);
      
      console.log(`Row ${index}, ${dayField}: value="${hourValue}", parsed=${hours}, original=${originalHours}`);
      
      // Include if current hours > 0 OR original hours were > 0 (to handle updates from positive to 0)
      if (hours > 0 || (hours == 0 && originalHours > 0)) {
        const dayNum = fieldToDayNum[dayField];
        
        const actualHoursEntry: ScheduleApiEntry = {
          employee: row.employee!.label || row.employee!.value,
          employeeGpid: row.employee!.gpid || row.employee!.id,
          role: row.role!.label || row.role!.value,
          roleId: row.role!.roleId ?? null,
          dayNum: dayNum,
          schdHours: 0, // Default to 0 for actual hours data
          actualHours: hours, // Set actual hours from the row data (including 0s)
          shift: shiftData ? {
            shift_type: shiftData.shift_type,
            time_format: shiftData.time_format,
            time_value: normalizeTimeValue(shiftData.time_value)
          } : null,
          locId: locationData?.locId,
          locationName: locationData?.locationName,
          period: periodData?.period,
          week: periodData?.week,
          year: periodData?.year
        };

        console.log(`Adding actual hours entry for ${dayField}:`, actualHoursEntry);
        result.push(actualHoursEntry);
      }
    });
  });

  console.log('Final result array:', result);
  console.log('Transformed actual hours employee rows to API format:', result);
  
  // Handle deleted rows - only include entries that had hours > 0, and reset them to 0
  if (deletedRows && deletedRows.length > 0) {
    console.log('Processing deleted actual hours employee rows:', deletedRows);
    
    deletedRows.forEach((row) => {
      if (!row.employee || !row.role) {
        console.warn('Skipping deleted actual hours row with missing employee or role:', row);
        return;
      }

      const shiftData = getShiftData(row.shift);

      // Process each day of the week
      Object.entries(fieldToDayNum).forEach(([field, dayNum]) => {
        const dayValue = row[field as keyof EmployeeRowData];
        const hoursValue = typeof dayValue === 'string' ? parseFloat(dayValue) || 0 : 0;

        // Only include if the deleted row had hours > 0 for this day
        if (hoursValue > 0) {
          const entry: ScheduleApiEntry = {
            employee: row.employee!.label || row.employee!.value,
            employeeGpid: row.employee!.gpid || row.employee!.id,
            role: row.role!.label || row.role!.value,
            roleId: row.role!.roleId ?? null,
            dayNum: dayNum,
            schdHours: 0, // Default to 0 for actual hours data
            actualHours: 0, // Reset to 0 for deleted rows
            shift: shiftData ? {
              shift_type: shiftData.shift_type,
              time_format: shiftData.time_format,
              time_value: normalizeTimeValue(shiftData.time_value)
            } : null,
            locId: locationData?.locId,
            locationName: locationData?.locationName,
            period: periodData?.period,
            week: periodData?.week,
            year: periodData?.year
          };

          result.push(entry);
          console.log(`Added deleted actual hours row entry for ${row.employee!.label} - ${row.role!.label} on day ${dayNum} (reset to 0 hours)`);
        }
      });
    });
  }
  
  return result;
};

/**
 * Transforms existing schedule API response to employee rows
 */
export const transformScheduleApiToEmployeeRows = (
  scheduleApiData: any[],
  employeeOptions: DLODropdownOption[],
  roleOptions: DLODropdownOption[],
  shiftOptions: DLODropdownOption[]
): EmployeeRowData[] => {
  if (!scheduleApiData || !Array.isArray(scheduleApiData) || scheduleApiData.length === 0) {
    return [];
  }

  console.log('Transforming schedule API data to employee rows:', scheduleApiData);
  console.log('Available employees:', employeeOptions);
  console.log('Available roles:', roleOptions);
  console.log('Available shifts:', shiftOptions);

  // Map dayNum to weekday field names
  const dayNumToField: { [key: number]: string } = {
    1: 'sunday',
    2: 'monday',
    3: 'tuesday',
    4: 'wednesday',
    5: 'thursday',
    6: 'friday',
    7: 'saturday'
  };

  // Helper function to create a consistent shift key for grouping
  const getShiftKey = (shift: any): string => {
    if (!shift) return 'no-shift';
    if (typeof shift === 'string') {
      const raw = shift.trim();
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.time_value) {
            return `${parsed.time_value}-${parsed.shift_type || ''}-${parsed.time_format || ''}`;
          }
        } catch {
          // Fall back to raw string key.
        }
      }
      return shift;
    }
    if (typeof shift === 'object' && shift.time_value) {
      return `${shift.time_value}-${shift.shift_type || ''}-${shift.time_format || ''}`;
    }
    return JSON.stringify(shift);
  };

  // Group data by unique employee-role-shift combinations
  const employeeRoleShiftMap: { [key: string]: any } = {};

  scheduleApiData.forEach((item: any) => {
    // Create a unique key for employee-role-shift combination
    // Use roleId for matching (more reliable than role name which may have casing differences)
    const shiftKey = getShiftKey(item.shift);
    const roleKey = item.roleId != null ? String(item.roleId) : item.role;
    const key = `${item.employeeGpid ?? item.employee}-${roleKey}-${shiftKey}`;

    if (!employeeRoleShiftMap[key]) {
      employeeRoleShiftMap[key] = {
        employee: item.employee,
        employeeEmail: item.employeeEmail,
        employeeGpid: item.employeeGpid ?? item.GPID ?? null,
        role: item.role,
        roleId: item.roleId ?? null,
        shift: item.shift,
        locId: item.locId,
        locationName: item.locationName,
        scheduleHours: {
          sunday: '0',
          monday: '0',
          tuesday: '0',
          wednesday: '0',
          thursday: '0',
          friday: '0',
          saturday: '0'
        },
        actualHours: {
          sunday: '0',
          monday: '0',
          tuesday: '0',
          wednesday: '0',
          thursday: '0',
          friday: '0',
          saturday: '0'
        },
        items: [] // Store all items for this combination
      };
    }

    // Map dayNum to correct weekday field
    const dayField = dayNumToField[item.dayNum];
    if (dayField) {
      employeeRoleShiftMap[key].scheduleHours[dayField] = (item.schdHours || 0).toString();
      employeeRoleShiftMap[key].actualHours[dayField] = (item.actualHours || 0).toString();
    }

    // Store the item for reference
    employeeRoleShiftMap[key].items.push(item);
  });

  console.log('Grouped employee-role-shift combinations:', employeeRoleShiftMap);

  // Group by employee to detect split roles
  const employeeGroups: { [employee: string]: any[] } = {};
  
  Object.values(employeeRoleShiftMap).forEach((combo: any) => {
    // Skip orphan combos where the API didn't provide an employee name.
    // These produce rows with employee=null + role/shift set, which the
    // incomplete-row validator flags and the user can't always reach in the UI
    // (e.g. invisible split-child rows), permanently disabling Save.
    if (!combo.employee || (typeof combo.employee === 'string' && combo.employee.trim() === '')) {
      console.log(`Skipping combination with no employee:`, combo);
      return;
    }

    // Skip combinations where all scheduled hours are 0
    const allScheduleHoursZero = Object.values(combo.scheduleHours).every(
      (hours: any) => parseFloat(hours) === 0
    );
    
    if (allScheduleHoursZero) {
      console.log(`Skipping combination with all zero scheduled hours: ${combo.employee} - ${combo.role}`);
      return;
    }
    
    if (!employeeGroups[combo.employee]) {
      employeeGroups[combo.employee] = [];
    }
    employeeGroups[combo.employee].push(combo);
  });

  console.log('Employee groups for split role detection:', employeeGroups);

  const employeeRows: EmployeeRowData[] = [];
  let rowIdCounter = 1;

  // Process each employee group
  Object.keys(employeeGroups).forEach(employeeName => {
    const employeeCombinations = employeeGroups[employeeName];

    if (employeeCombinations.length === 1) {
      // Single role for this employee
      const combo = employeeCombinations[0];
      const employeeRow = createEmployeeRowFromCombo(combo, rowIdCounter.toString(), employeeOptions, roleOptions, shiftOptions);
      if (employeeRow) {
        employeeRows.push(employeeRow);
        rowIdCounter++;
      }
    } else {
      // Multiple roles for this employee - create split roles
      const parentId = rowIdCounter.toString();
      rowIdCounter++;

      // Create parent row (first combination)
      const firstCombo = employeeCombinations[0];
      const parentRow = createEmployeeRowFromCombo(firstCombo, parentId, employeeOptions, roleOptions, shiftOptions, true);
      if (parentRow) {
        employeeRows.push(parentRow);

        // Create child rows for remaining combinations
        for (let i = 1; i < employeeCombinations.length; i++) {
          const childCombo = employeeCombinations[i];
          const childId = rowIdCounter.toString();
          const childRow = createEmployeeRowFromCombo(childCombo, childId, employeeOptions, roleOptions, shiftOptions, false, parentId);
          if (childRow) {
            employeeRows.push(childRow);
            rowIdCounter++;
          }
        }
      }
    }
  });

  console.log('Created employee rows:', employeeRows);
  return employeeRows;
};

/**
 * Helper function to create an EmployeeRowData from a combination
 */
const createEmployeeRowFromCombo = (
  combo: any,
  id: string,
  employeeOptions: DLODropdownOption[],
  roleOptions: DLODropdownOption[],
  shiftOptions: DLODropdownOption[],
  isSplit: boolean = false,
  parentId?: string
): EmployeeRowData | null => {
  // Find matching employee option — match by GPID first (coerce to string), fall back to name
  const gpid = combo.employeeGpid != null ? String(combo.employeeGpid) : null;
  const employeeOption = employeeOptions.find(emp =>
    (gpid && (
      String(emp.gpid) === gpid ||
      String(emp.value) === gpid ||
      String(emp.id) === gpid
    )) ||
    (combo.employeeEmail && (emp.value === combo.employeeEmail || emp.id === combo.employeeEmail)) ||
    emp.label === combo.employee ||
    emp.label?.startsWith(combo.employee + ' ')
  );

  // Find matching role option — match by roleId first, fall back to name
  const roleOption = roleOptions.find(role =>
    combo.roleId != null
      ? role.roleId === combo.roleId || role.id === String(combo.roleId)
      : role.label === combo.role || role.value === combo.role || role.id === combo.role
  ) || (combo.role ? {
    // Fallback: role may be disabled/deleted — preserve roleId from the loaded schedule record
    id: combo.roleId != null ? String(combo.roleId) : combo.role,
    label: combo.role,
    value: combo.roleId != null ? String(combo.roleId) : combo.role,
    roleId: combo.roleId ?? null,
  } : null);

  // Find matching shift option - handle both string and object formats from API
  let shiftOption: DLODropdownOption | null = null;

  const parseScheduleShift = (shift: any): { timeStr: string; shiftType: string; timeFormat: string } => {
    if (!shift) {
      return { timeStr: '', shiftType: '', timeFormat: '' };
    }

    // Handle object format: { shift_type, time_format, time_value }
    if (typeof shift === 'object' && shift.time_value) {
      return {
        timeStr: shift.time_value,
        shiftType: shift.shift_type || '',
        timeFormat: shift.time_format || '',
      };
    }

    // Handle JSON string format from schedule API: "{\"shift_type\":...,\"time_value\":...}"
    if (typeof shift === 'string') {
      const raw = shift.trim();
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.time_value) {
            return {
              timeStr: parsed.time_value,
              shiftType: parsed.shift_type || '',
              timeFormat: parsed.time_format || '',
            };
          }
        } catch {
          // Fall through to legacy plain-time handling.
        }
      }

      // Legacy plain time format: "08:00:00"
      return { timeStr: raw, shiftType: '', timeFormat: '' };
    }

    return { timeStr: '', shiftType: '', timeFormat: '' };
  };
  
  if (combo.shift) {
    const { timeStr, shiftType, timeFormat } = parseScheduleShift(combo.shift);

    if (timeStr) {
      // First try to find exact match (time_value + shift_type + time_format if available)
      if (shiftType || timeFormat) {
        // Try matching time, shift_type, and time_format for most accurate match
        shiftOption = shiftOptions.find(shift => {
          try {
            const shiftData = JSON.parse(shift.value);
            const timeMatch = shiftData.time_value && shiftData.time_value.startsWith(timeStr);
            
            if (!timeMatch) return false;
            
            // Check shift_type match if provided
            const typeMatch = !shiftType || !shiftData.shift_type || 
              shiftData.shift_type.toLowerCase() === shiftType.toLowerCase();
            
            // Check time_format (AM/PM) match if provided
            const formatMatch = !timeFormat || !shiftData.time_format ||
              shiftData.time_format.toLowerCase() === timeFormat.toLowerCase();
            
            return typeMatch && formatMatch;
          } catch (e) {
            return false;
          }
        }) || null;
      }

      // If no match with shift_type/time_format, fall back to time-only matching
      if (!shiftOption) {
        shiftOption = shiftOptions.find(shift => {
          try {
            const shiftData = JSON.parse(shift.value);
            return shiftData.time_value && shiftData.time_value.startsWith(timeStr);
          } catch (e) {
            // Fallback for non-JSON shift values
            return shift.label.includes(timeStr) || shift.value.includes(timeStr);
          }
        }) || null;
      }

      // If still no match, try to find by hour only (remove minutes and seconds)
      if (!shiftOption) {
        const hourOnly = timeStr.split(':')[0];
        
        // Try with shift_type and time_format first if available
        if (shiftType || timeFormat) {
          shiftOption = shiftOptions.find(shift => {
            try {
              const shiftData = JSON.parse(shift.value);
              const timeMatch = shiftData.time_value && shiftData.time_value.startsWith(hourOnly + ':');
              
              if (!timeMatch) return false;
              
              const typeMatch = !shiftType || !shiftData.shift_type ||
                shiftData.shift_type.toLowerCase() === shiftType.toLowerCase();
              const formatMatch = !timeFormat || !shiftData.time_format ||
                shiftData.time_format.toLowerCase() === timeFormat.toLowerCase();
              
              return typeMatch && formatMatch;
            } catch (e) {
              return false;
            }
          }) || null;
        }
        
        // Fall back to hour-only matching
        if (!shiftOption) {
          shiftOption = shiftOptions.find(shift => {
            try {
              const shiftData = JSON.parse(shift.value);
              return shiftData.time_value && shiftData.time_value.startsWith(hourOnly + ':');
            } catch (e) {
              return shift.label.includes(hourOnly + ':') || shift.value.includes(hourOnly + ':');
            }
          }) || null;
        }
      }
    }
  }

  const backendEmployeeGpid = gpid || (combo.employeeGpid != null ? String(combo.employeeGpid) : '') || (combo.employee ? String(combo.employee) : '');
  const backendEmployeeOption: DLODropdownOption | null = backendEmployeeGpid || combo.employee
    ? {
        id: backendEmployeeGpid,
        label: combo.employee || backendEmployeeGpid,
        value: backendEmployeeGpid,
        gpid: backendEmployeeGpid,
      }
    : null;

  const backendRoleOption: DLODropdownOption | null = combo.role
    ? {
        id: combo.roleId != null ? String(combo.roleId) : String(combo.role),
        label: combo.role,
        value: combo.roleId != null ? String(combo.roleId) : String(combo.role),
        roleId: combo.roleId ?? null,
      }
    : null;

  const backendShiftParsed = parseScheduleShift(combo.shift);
  let matchedShiftData: any = null;
  if (shiftOption?.value) {
    try {
      matchedShiftData = JSON.parse(shiftOption.value);
    } catch {
      matchedShiftData = null;
    }
  }
  const backendShiftOption: DLODropdownOption | null = backendShiftParsed.timeStr
    ? {
        id: shiftOption?.id || `${backendShiftParsed.timeStr}-${backendShiftParsed.shiftType}-${backendShiftParsed.timeFormat}`,
        label: shiftOption?.label || combo.shift?.label || backendShiftParsed.timeStr,
        value: JSON.stringify({
          id: shiftOption?.id || `${backendShiftParsed.timeStr}-${backendShiftParsed.shiftType}-${backendShiftParsed.timeFormat}`,
          shift_type: matchedShiftData?.shift_type || backendShiftParsed.shiftType,
          time_format: matchedShiftData?.time_format || backendShiftParsed.timeFormat,
          time_value: matchedShiftData?.time_value || backendShiftParsed.timeStr,
        }),
      }
    : null;

  // Calculate total hours
  const weekdayHours = [
    combo.scheduleHours.sunday,
    combo.scheduleHours.monday,
    combo.scheduleHours.tuesday,
    combo.scheduleHours.wednesday,
    combo.scheduleHours.thursday,
    combo.scheduleHours.friday,
    combo.scheduleHours.saturday
  ];
  
  const totalHours = weekdayHours.reduce((sum: number, hours: string) => {
    return sum + (parseFloat(hours) || 0);
  }, 0);

  const employeeRow: EmployeeRowData = {
    id,
    isSelected: false,
    employee: employeeOption || null,
    role: roleOption || null,
    shift: shiftOption || null,
    sunday: combo.scheduleHours.sunday,
    monday: combo.scheduleHours.monday,
    tuesday: combo.scheduleHours.tuesday,
    wednesday: combo.scheduleHours.wednesday,
    thursday: combo.scheduleHours.thursday,
    friday: combo.scheduleHours.friday,
    saturday: combo.scheduleHours.saturday,
    total: totalHours.toString(),
    isSplit: isSplit && !parentId, // Only parent rows are marked as split
    isChildRow: !!parentId,
    parentId: parentId || undefined,
    isLocked: false,
    sourceSnapshot: {
      employee: backendEmployeeOption,
      role: backendRoleOption,
      shift: backendShiftOption,
      sunday: combo.scheduleHours.sunday,
      monday: combo.scheduleHours.monday,
      tuesday: combo.scheduleHours.tuesday,
      wednesday: combo.scheduleHours.wednesday,
      thursday: combo.scheduleHours.thursday,
      friday: combo.scheduleHours.friday,
      saturday: combo.scheduleHours.saturday,
    },
  };

  console.log(`Created employee row for ${combo.employee} - ${combo.role}:`, employeeRow);

  return employeeRow;
};

/**
 * Transforms existing schedule API response to actual hours employee rows
 */
export const transformScheduleApiToActualEmployeeRows = (
  scheduleApiData: any[],
  employeeOptions: DLODropdownOption[],
  roleOptions: DLODropdownOption[],
  shiftOptions: DLODropdownOption[]
): EmployeeRowData[] => {
  if (!scheduleApiData || !Array.isArray(scheduleApiData) || scheduleApiData.length === 0) {
    return [];
  }

  console.log('Transforming schedule API data to actual hours employee rows:', scheduleApiData);

  // Map dayNum to weekday field names
  const dayNumToField: { [key: number]: string } = {
    1: 'sunday',
    2: 'monday',
    3: 'tuesday',
    4: 'wednesday',
    5: 'thursday',
    6: 'friday',
    7: 'saturday'
  };

  // Helper function to create a consistent shift key for grouping
  const getShiftKey = (shift: any): string => {
    if (!shift) return 'no-shift';
    if (typeof shift === 'string') {
      const raw = shift.trim();
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.time_value) {
            return `${parsed.time_value}-${parsed.shift_type || ''}-${parsed.time_format || ''}`;
          }
        } catch {
          // Fall back to raw string key.
        }
      }
      return shift;
    }
    if (typeof shift === 'object' && shift.time_value) {
      return `${shift.time_value}-${shift.shift_type || ''}-${shift.time_format || ''}`;
    }
    return JSON.stringify(shift);
  };

  // Group data by unique employee-role-shift combinations
  const employeeRoleShiftMap: { [key: string]: any } = {};

  scheduleApiData.forEach((item: any) => {
    // Create a unique key for employee-role-shift combination
    // Use roleId for matching (more reliable than role name which may have casing differences)
    const shiftKey = getShiftKey(item.shift);
    const roleKey = item.roleId != null ? String(item.roleId) : item.role;
    const key = `${item.employeeGpid ?? item.employee}-${roleKey}-${shiftKey}`;

    if (!employeeRoleShiftMap[key]) {
      employeeRoleShiftMap[key] = {
        employee: item.employee,
        employeeEmail: item.employeeEmail,
        employeeGpid: item.employeeGpid ?? item.GPID ?? null,
        role: item.role,
        roleId: item.roleId ?? null,
        shift: item.shift,
        locId: item.locId,
        locationName: item.locationName,
        actualHours: {
          sunday: '0',
          monday: '0',
          tuesday: '0',
          wednesday: '0',
          thursday: '0',
          friday: '0',
          saturday: '0'
        },
        overrideActualHours: {
          sunday: null,
          monday: null,
          tuesday: null,
          wednesday: null,
          thursday: null,
          friday: null,
          saturday: null
        },
        items: [] // Store all items for this combination
      };
    }

    // Map dayNum to correct weekday field using actualHours
    const dayField = dayNumToField[item.dayNum];
    if (dayField) {
      employeeRoleShiftMap[key].actualHours[dayField] = (item.actualHours || 0).toString();
      
      // Handle override actual hours if present in API data
      if (item.overrideActualHours !== undefined && item.overrideActualHours !== null) {
        employeeRoleShiftMap[key].overrideActualHours[dayField] = item.overrideActualHours;
      }
    }

    // Store the item for reference
    employeeRoleShiftMap[key].items.push(item);
  });

  console.log('Grouped employee-role-shift combinations for actual hours:', employeeRoleShiftMap);

  // Group by employee to detect split roles
  const employeeGroups: { [employee: string]: any[] } = {};
  
  Object.values(employeeRoleShiftMap).forEach((combo: any) => {
    // Skip orphan combos where the API didn't provide an employee name.
    if (!combo.employee || (typeof combo.employee === 'string' && combo.employee.trim() === '')) {
      console.log(`Skipping actual combination with no employee:`, combo);
      return;
    }

    // Skip combinations where all actual hours are 0
    const allActualHoursZero = Object.values(combo.actualHours).every(
      (hours: any) => parseFloat(hours) === 0
    );
    
    if (allActualHoursZero) {
      console.log(`Skipping combination with all zero actual hours: ${combo.employee} - ${combo.role}`);
      return;
    }
    
    if (!employeeGroups[combo.employee]) {
      employeeGroups[combo.employee] = [];
    }
    employeeGroups[combo.employee].push(combo);
  });

  console.log('Employee groups for actual hours split role detection:', employeeGroups);

  const employeeRows: EmployeeRowData[] = [];
  let rowIdCounter = 1;

  // Process each employee group
  Object.keys(employeeGroups).forEach(employeeName => {
    const employeeCombinations = employeeGroups[employeeName];

    if (employeeCombinations.length === 1) {
      // Single role for this employee
      const combo = employeeCombinations[0];
      const employeeRow = createActualEmployeeRowFromCombo(combo, rowIdCounter.toString(), employeeOptions, roleOptions, shiftOptions);
      if (employeeRow) {
        employeeRows.push(employeeRow);
        rowIdCounter++;
      }
    } else {
      // Multiple roles for this employee - create split roles
      const parentId = rowIdCounter.toString();
      rowIdCounter++;

      // Create parent row (first combination)
      const firstCombo = employeeCombinations[0];
      const parentRow = createActualEmployeeRowFromCombo(firstCombo, parentId, employeeOptions, roleOptions, shiftOptions, true);
      if (parentRow) {
        employeeRows.push(parentRow);

        // Create child rows for remaining combinations
        for (let i = 1; i < employeeCombinations.length; i++) {
          const childCombo = employeeCombinations[i];
          const childId = rowIdCounter.toString();
          const childRow = createActualEmployeeRowFromCombo(childCombo, childId, employeeOptions, roleOptions, shiftOptions, false, parentId);
          if (childRow) {
            employeeRows.push(childRow);
            rowIdCounter++;
          }
        }
      }
    }
  });

  console.log('Created actual hours employee rows:', employeeRows);
  return employeeRows;
};

/**
 * Helper function to create an EmployeeRowData from a combination for actual hours
 */
const createActualEmployeeRowFromCombo = (
  combo: any,
  id: string,
  employeeOptions: DLODropdownOption[],
  roleOptions: DLODropdownOption[],
  shiftOptions: DLODropdownOption[],
  isSplit: boolean = false,
  parentId?: string
): EmployeeRowData | null => {
  // Skip rows where the API didn't provide an employee name. Otherwise we'd
  // create a row with employee=null + role/shift set, which the incomplete-row
  // validator flags and permanently disables Save.
  if (!combo.employee || (typeof combo.employee === 'string' && combo.employee.trim() === '')) {
    console.log(`Skipping actual row — no employee provided:`, combo);
    return null;
  }

  // Find matching employee option — match by GPID first (coerce to string), fall back to name
  const gpid = combo.employeeGpid != null ? String(combo.employeeGpid) : null;
  const employeeOption = employeeOptions.find(emp =>
    (gpid && (
      String(emp.gpid) === gpid ||
      String(emp.value) === gpid ||
      String(emp.id) === gpid
    )) ||
    (combo.employeeEmail && (emp.value === combo.employeeEmail || emp.id === combo.employeeEmail)) ||
    emp.label === combo.employee ||
    emp.label?.startsWith(combo.employee + ' ')
  );

  // Find matching role option — match by roleId first, fall back to name
  const roleOption = roleOptions.find(role =>
    combo.roleId != null
      ? role.roleId === combo.roleId || role.id === String(combo.roleId)
      : role.label === combo.role || role.value === combo.role || role.id === combo.role
  ) || (combo.role ? {
    // Fallback: role may be disabled/deleted — preserve roleId from the loaded schedule record
    id: combo.roleId != null ? String(combo.roleId) : combo.role,
    label: combo.role,
    value: combo.roleId != null ? String(combo.roleId) : combo.role,
    roleId: combo.roleId ?? null,
  } : null);

  // Find matching shift option - handle both string and object formats from API
  let shiftOption: DLODropdownOption | null = null;

  const parseScheduleShift = (shift: any): { timeStr: string; shiftType: string; timeFormat: string } => {
    if (!shift) {
      return { timeStr: '', shiftType: '', timeFormat: '' };
    }

    // Handle object format: { shift_type, time_format, time_value }
    if (typeof shift === 'object' && shift.time_value) {
      return {
        timeStr: shift.time_value,
        shiftType: shift.shift_type || '',
        timeFormat: shift.time_format || '',
      };
    }

    // Handle JSON string format from schedule API: "{\"shift_type\":...,\"time_value\":...}"
    if (typeof shift === 'string') {
      const raw = shift.trim();
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.time_value) {
            return {
              timeStr: parsed.time_value,
              shiftType: parsed.shift_type || '',
              timeFormat: parsed.time_format || '',
            };
          }
        } catch {
          // Fall through to legacy plain-time handling.
        }
      }

      // Legacy plain time format: "08:00:00"
      return { timeStr: raw, shiftType: '', timeFormat: '' };
    }

    return { timeStr: '', shiftType: '', timeFormat: '' };
  };
  
  if (combo.shift) {
    const { timeStr, shiftType, timeFormat } = parseScheduleShift(combo.shift);

    if (timeStr) {
      // First try to find exact match (time_value + shift_type + time_format if available)
      if (shiftType || timeFormat) {
        // Try matching time, shift_type, and time_format for most accurate match
        shiftOption = shiftOptions.find(shift => {
          try {
            const shiftData = JSON.parse(shift.value);
            const timeMatch = shiftData.time_value && shiftData.time_value.startsWith(timeStr);
            
            if (!timeMatch) return false;
            
            // Check shift_type match if provided
            const typeMatch = !shiftType || !shiftData.shift_type || 
              shiftData.shift_type.toLowerCase() === shiftType.toLowerCase();
            
            // Check time_format (AM/PM) match if provided
            const formatMatch = !timeFormat || !shiftData.time_format ||
              shiftData.time_format.toLowerCase() === timeFormat.toLowerCase();
            
            return typeMatch && formatMatch;
          } catch (e) {
            return false;
          }
        }) || null;
      }

      // If no match with shift_type/time_format, fall back to time-only matching
      if (!shiftOption) {
        shiftOption = shiftOptions.find(shift => {
          try {
            const shiftData = JSON.parse(shift.value);
            return shiftData.time_value && shiftData.time_value.startsWith(timeStr);
          } catch (e) {
            // Fallback for non-JSON shift values
            return shift.label.includes(timeStr) || shift.value.includes(timeStr);
          }
        }) || null;
      }

      // If still no match, try to find by hour only (remove minutes and seconds)
      if (!shiftOption) {
        const hourOnly = timeStr.split(':')[0];
        
        // Try with shift_type and time_format first if available
        if (shiftType || timeFormat) {
          shiftOption = shiftOptions.find(shift => {
            try {
              const shiftData = JSON.parse(shift.value);
              const timeMatch = shiftData.time_value && shiftData.time_value.startsWith(hourOnly + ':');
              
              if (!timeMatch) return false;
              
              const typeMatch = !shiftType || !shiftData.shift_type ||
                shiftData.shift_type.toLowerCase() === shiftType.toLowerCase();
              const formatMatch = !timeFormat || !shiftData.time_format ||
                shiftData.time_format.toLowerCase() === timeFormat.toLowerCase();
              
              return typeMatch && formatMatch;
            } catch (e) {
              return false;
            }
          }) || null;
        }
        
        // Fall back to hour-only matching
        if (!shiftOption) {
          shiftOption = shiftOptions.find(shift => {
            try {
              const shiftData = JSON.parse(shift.value);
              return shiftData.time_value && shiftData.time_value.startsWith(hourOnly + ':');
            } catch (e) {
              return shift.label.includes(hourOnly + ':') || shift.value.includes(hourOnly + ':');
            }
          }) || null;
        }
      }
    }
  }

  const backendEmployeeGpid = gpid || (combo.employeeGpid != null ? String(combo.employeeGpid) : '') || (combo.employee ? String(combo.employee) : '');
  const backendEmployeeOption: DLODropdownOption | null = backendEmployeeGpid || combo.employee
    ? {
        id: backendEmployeeGpid,
        label: combo.employee || backendEmployeeGpid,
        value: backendEmployeeGpid,
        gpid: backendEmployeeGpid,
      }
    : null;

  const backendRoleOption: DLODropdownOption | null = combo.role
    ? {
        id: combo.roleId != null ? String(combo.roleId) : String(combo.role),
        label: combo.role,
        value: combo.roleId != null ? String(combo.roleId) : String(combo.role),
        roleId: combo.roleId ?? null,
      }
    : null;

  const backendShiftParsed = parseScheduleShift(combo.shift);
  let matchedShiftData: any = null;
  if (shiftOption?.value) {
    try {
      matchedShiftData = JSON.parse(shiftOption.value);
    } catch {
      matchedShiftData = null;
    }
  }
  const backendShiftOption: DLODropdownOption | null = backendShiftParsed.timeStr
    ? {
        id: shiftOption?.id || `${backendShiftParsed.timeStr}-${backendShiftParsed.shiftType}-${backendShiftParsed.timeFormat}`,
        label: shiftOption?.label || combo.shift?.label || backendShiftParsed.timeStr,
        value: JSON.stringify({
          id: shiftOption?.id || `${backendShiftParsed.timeStr}-${backendShiftParsed.shiftType}-${backendShiftParsed.timeFormat}`,
          shift_type: matchedShiftData?.shift_type || backendShiftParsed.shiftType,
          time_format: matchedShiftData?.time_format || backendShiftParsed.timeFormat,
          time_value: matchedShiftData?.time_value || backendShiftParsed.timeStr,
        }),
      }
    : null;

  // Calculate total hours from actual hours
  const weekdayHours = [
    combo.actualHours.sunday,
    combo.actualHours.monday,
    combo.actualHours.tuesday,
    combo.actualHours.wednesday,
    combo.actualHours.thursday,
    combo.actualHours.friday,
    combo.actualHours.saturday
  ];
  
  const totalHours = weekdayHours.reduce((sum: number, hours: string) => {
    return sum + (parseFloat(hours) || 0);
  }, 0);

  const employeeRow: EmployeeRowData = {
    id,
    isSelected: false,
    employee: employeeOption || null,
    role: roleOption || null,
    shift: shiftOption || null,
    sunday: combo.actualHours.sunday,
    monday: combo.actualHours.monday,
    tuesday: combo.actualHours.tuesday,
    wednesday: combo.actualHours.wednesday,
    thursday: combo.actualHours.thursday,
    friday: combo.actualHours.friday,
    saturday: combo.actualHours.saturday,
    total: totalHours.toString(),
    isSplit: isSplit && !parentId, // Only parent rows are marked as split
    isChildRow: !!parentId,
    parentId: parentId || undefined,
    isLocked: false,
    // Add override actual hours for compatibility with ActualHoursEmployeeRowData
    overrideActualHours: combo.overrideActualHours,
    sourceSnapshot: {
      employee: backendEmployeeOption,
      role: backendRoleOption,
      shift: backendShiftOption,
      sunday: combo.actualHours.sunday,
      monday: combo.actualHours.monday,
      tuesday: combo.actualHours.tuesday,
      wednesday: combo.actualHours.wednesday,
      thursday: combo.actualHours.thursday,
      friday: combo.actualHours.friday,
      saturday: combo.actualHours.saturday,
    },
  } as any; // Cast to any to allow the extra property

  console.log(`Created actual hours employee row for ${combo.employee} - ${combo.role}:`, employeeRow);

  return employeeRow;
};
